import IconCopy from "assets/img/icon-copy.svg?react"

import React, { FunctionComponent } from "react"
import { useTranslation } from "react-i18next"

import { ArticleAny } from "@/types"
import { Flex, FlexItem } from "components/Flex"
import Text from "components/Text/Text"
import InfoButton from "components/InfoButton/InfoButton"
import Lmid from "components/Lmid/Lmid"
import ArticleDescription from "components/ArticleDescription/ArticleDescription"
import AssetStatusTag from "components/AssetStatusTag/AssetStatusTag"
import AssetStoredStatus from "components/AssetStoredStatus/AssetStoredStatus"
import { AssetStoredStatus as AssetStoredStatusEnum } from "models/AssetDisplayStatus"
import articleSidebars from "stores/articleSidebars"
import Asset, { AssetOwnershipType } from "models/Asset"
import Radio from "components/Radio/Radio"
import Button from "components/Button/Button"
import useCopyToClipboard from "components/CopyToClipboard/useCopyToClipboard"

export interface ICopyAssetHeaderProps {
    asset: Asset
    article: ArticleAny
    ownershipType: AssetOwnershipType
    /** Initial condition chosen for the copies in the ownership step. */
    isSecondhand: boolean
    storedStatus?: AssetStoredStatusEnum | null
}

/**
 * Read-only strip on the dialog's title line showing the source asset the
 * copies are derived from: the article description with the article-info
 * action, and below it the LMID with copy action, status tag, state of
 * storage, and the ownership and initial condition chosen for the copies.
 */
const CopyAssetHeader: FunctionComponent<React.PropsWithChildren<ICopyAssetHeaderProps>> = ({
    asset,
    article,
    ownershipType,
    isSecondhand,
    storedStatus,
}) => {
    const { t } = useTranslation()
    const copyToClipboard = useCopyToClipboard()

    return (
        <div className="copy-asset__header">
            <Flex alignItems="center" gap="0.8rem" className="min-w-0">
                <Text type="h2" className="min-w-0">
                    <ArticleDescription
                        articleDescription={asset.altArticleDescription || article.description}
                        clamp={1}
                    />
                </Text>
                <InfoButton
                    onClick={() => articleSidebars.show({ articleId: article.id! })}
                />
            </Flex>
            <Flex alignItems="center" gap="1.6rem" className="min-w-0 mt-1">
                <FlexItem shrink={0}>
                    <Flex alignItems="center" gap="0.4rem">
                        <Text type="p">{t('common.lmid')}:</Text>
                        <Lmid
                            lmid={asset.lmid}
                            assetId={asset.id}
                            labeled={asset.isLabeled}
                            inCollection={asset.linkedToAssetCollection}
                        />
                        <Button
                            theme="link"
                            size="small"
                            ariaLabel={t('common.copy')}
                            icon={<IconCopy />}
                            onClick={() => copyToClipboard({ text: asset.lmid })}
                        />
                    </Flex>
                </FlexItem>
                <FlexItem shrink={0}>
                    <AssetStatusTag status={asset.status} />
                </FlexItem>
                {!!storedStatus && storedStatus !== AssetStoredStatusEnum.notApplicable &&
                    <FlexItem shrink={0}>
                        <AssetStoredStatus status={storedStatus} />
                    </FlexItem>
                }
                <Radio
                    label={t('lmidModal.purchasedAsset')}
                    checked={ownershipType === AssetOwnershipType.purchased}
                    disabled
                    name="copyAssetHeaderOwnership"
                />
                <Radio
                    label={t('lmidModal.borrowedAsset')}
                    checked={ownershipType === AssetOwnershipType.borrowed}
                    disabled
                    name="copyAssetHeaderOwnership"
                />
                <Radio
                    label={t('asset.newAsset')}
                    checked={!isSecondhand}
                    disabled
                    name="copyAssetHeaderCondition"
                />
                <Radio
                    label={t('asset.usedAsset')}
                    checked={isSecondhand}
                    disabled
                    name="copyAssetHeaderCondition"
                />
            </Flex>
        </div>
    )
}

export default CopyAssetHeader
